import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { auth, db } from "./firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { Container, Form, Button, ListGroup, Image } from "react-bootstrap";
import { BsSend } from "react-icons/bs";

function GroupChatRoom() {
  const { groupId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [user, setUser] = useState(null);
  const [groupName, setGroupName] = useState("グループ");
  const [userImage, setUserImage] = useState("default-profile.png");
  const [members, setMembers] = useState([]);

  // ユーザー認証を確認し、グループ情報を取得
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setUserImage(currentUser.photoURL || "default-profile.png");
        fetchGroupInfo(groupId); // グループ情報を取得
      }
    });
    return () => unsubscribeAuth();
  }, [groupId]);

  // メッセージの取得
  useEffect(() => {
    if (user) {
      fetchMessages(); // ユーザーが認証された後にメッセージを取得
    }
  }, [user, groupId]);

  // メッセージを既読にマーク
  useEffect(() => {
    if (user && messages.length > 0) {
      markMessagesAsRead();
    }
  }, [user, groupId, messages]);

  // グループ情報をFirestoreから取得
  const fetchGroupInfo = async (groupId) => {
    try {
      const groupRef = doc(db, "groups", groupId);
      const groupSnap = await getDoc(groupRef);
      if (groupSnap.exists()) {
        setGroupName(groupSnap.data().name || "グループ");
        setMembers(groupSnap.data().members || []);
      }
    } catch (error) {
      console.error("グループ情報の取得エラー:", error);
    }
  };

  // メッセージをFirestoreから取得
  const fetchMessages = () => {
    const messagesRef = collection(db, "groups", groupId, "messages");
    const q = query(messagesRef, orderBy("timestamp"));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const messagesWithImages = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const msgData = doc.data();
          const senderImage = await fetchMemberInfo(msgData.sender); // 送信者のプロフィール画像を取得
          return {
            id: doc.id,
            ...msgData,
            timestamp: msgData.timestamp ? msgData.timestamp.toDate() : new Date(),
            senderImage,
          };
        })
      );
      setMessages(messagesWithImages);
    });

    return () => unsubscribe();
  };

  // 送信者のプロフィール画像を取得
  const fetchMemberInfo = async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        return userData.profileImage || "default-profile.png"; // プロフィール画像があればそれを返す
      }
    } catch (error) {
      console.error("ユーザー情報の取得エラー:", error);
      return "default-profile.png"; // 取得できない場合はデフォルト画像
    }
  };

  // メッセージを既読にマーク
  const markMessagesAsRead = async () => {
    const messagesRef = collection(db, "groups", groupId, "messages");
    const q = query(messagesRef, orderBy("timestamp"));

    const snapshot = await getDocs(q);
    snapshot.docs.forEach(async (docItem) => {
      const data = docItem.data();
      // 現在のユーザーが送信者でなく、かつ `isRead` が false の場合のみ更新
      if (data.sender !== user.uid && !data.isRead[user.uid]) {
        await updateDoc(docItem.ref, {
          [`isRead.${user.uid}`]: true, // 現在のユーザーの `isRead` を true に更新
        });
      }
    });
  };

  // メッセージを送信
  const sendMessage = async () => {
    if (!newMessage.trim() || !user) return;

    try {
      const messagesRef = collection(db, "groups", groupId, "messages");
      const docRef = await addDoc(messagesRef, {
        sender: user.uid,
        senderName: user.displayName || "匿名",
        text: newMessage,
        timestamp: serverTimestamp(),
        isRead: members.reduce((acc, member) => {
          acc[member] = false; // すべてのメンバーに対して初期値は false
          return acc;
        }, {}),
      });
      console.log("メッセージ送信成功: ", docRef.id); // 送信したドキュメントのIDをログに表示
      setNewMessage("");
    } catch (error) {
      console.error("メッセージ送信エラー:", error);
    }
  };

  // 既読数を計算
  const calculateReadCount = (isRead) => {
    return Object.values(isRead).filter((readStatus) => readStatus === true).length;
  };

  // タイムスタンプのフォーマット
  const formatTimestamp = (timestamp) => {
    return timestamp?.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Container className="mt-4 p-4" style={{ backgroundColor: '#f7f7f7', borderRadius: '15px', minHeight: '80vh' }}>
      <h4 className="mb-3 text-center text-dark" style={{ fontWeight: 'bold' }}>{groupName}</h4>
      
      {/* メッセージリスト */}
      <ListGroup className="mb-3" style={{ maxHeight: '400px', overflowY: 'auto', borderRadius: '10px' }}>
        {messages.map((msg) => (
          <ListGroup.Item
            key={msg.id}
            className={`d-flex align-items-center border-0 ${msg.sender === user?.uid ? "justify-content-end" : "justify-content-start"}`}
            style={{ backgroundColor: "transparent" }}
          >
            {/* 送信者のプロフィール画像 */}
            {msg.sender !== user?.uid && (
              <Image src={msg.senderImage} roundedCircle width={40} height={40} className="me-2" />
            )}

            <div>
              <div
                className={`p-2 rounded-pill ${msg.sender === user?.uid ? "bg-success text-white" : "bg-light"}`}
                style={{ maxWidth: "60%", wordBreak: "break-word" }}
              >
                <p className="mb-0">{msg.text}</p>
              </div>
              <small
                className={`text-muted d-block mt-1 ${msg.sender === user?.uid ? "text-end" : "text-start"}`}
              >
                {msg.senderName}・{formatTimestamp(msg.timestamp)}
                {msg.sender === user?.uid && msg.isRead[user.uid] && " ✔既読"}
              </small>
              {/* 既読数の表示 */}
              <small className="text-muted d-block mt-1">
                既読 {calculateReadCount(msg.isRead)}
              </small>
            </div>

            {/* ユーザー自身のプロフィール画像 */}
            {msg.sender === user?.uid && (
              <Image src={userImage} roundedCircle width={40} height={40} className="ms-2" />
            )}
          </ListGroup.Item>
        ))}
      </ListGroup>

      {/* メッセージ入力フォーム */}
      <Form className="d-flex">
        <Form.Control
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="メッセージを入力..."
          className="rounded-pill me-2 p-2"
          style={{ border: '1px solid #ccc', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
        />
        <Button
          onClick={sendMessage}
          variant="primary"
          className="rounded-circle p-3"
          style={{ backgroundColor: '#007bff', border: 'none', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
        >
          <BsSend size={20} />
        </Button>
      </Form>
    </Container>
  );
}

export default GroupChatRoom;
